import { useCallback } from 'react';

import createCanonicalUrl from '~/pages/couples/explore/util/canonicalUrl';
import { setMostRecentVendorSearches } from '~/util/searchUtils';

import { PartialVendorSearch, VendorSearchRequest } from '../types/types';
import { useSearchResultsPageContext } from './SearchResultsPageContext';
import {
  NavigateToSrpFunction,
  VendorSearchNavigationContext,
} from './VendorSearchNavigationContext';

const useNavigateToSrp = () => {
  const { facetsByVendorType, setVendorSearch } = useSearchResultsPageContext();

  return useCallback(
    (vendorSearch?: VendorSearchRequest | null): NavigateToSrpFunction =>
      (change: PartialVendorSearch) => {
        const newSearch = { ...vendorSearch, ...change };

        // If the location or category changes, navigate to a new page
        if (
          vendorSearch?.selectedLocation?.slug !== newSearch?.selectedLocation?.slug ||
          vendorSearch?.selectedTaxonomy?.key !== newSearch?.selectedTaxonomy?.key
        ) {
          const to = createCanonicalUrl({
            selectedLocation: newSearch.selectedLocation,
            selectedTaxonomy: newSearch.selectedTaxonomy,
          });
          setMostRecentVendorSearches(to, newSearch.selectedTaxonomy?.key);
          window.location.assign(to);
          return;
        }

        // If the page hasn't changed, we're changing filters and should reset to page 1
        if (change.page === undefined) {
          newSearch.page = 1;
        }

        const to = createCanonicalUrl(newSearch, facetsByVendorType);

        // If the new URL is the same as the current URL, don't navigate
        if (to === window.location.pathname + window.location.search) return;

        setMostRecentVendorSearches(to, newSearch.selectedTaxonomy?.key);
        setVendorSearch(newSearch as VendorSearchRequest);
        window.history.pushState({}, '', to);
      },
    [facetsByVendorType, setVendorSearch]
  );
};

/**
 * Encapsulates the logic for navigating on the SRP
 */
export const VendorSearchNavigationProvider: React.FC = ({ children }) => {
  const { vendorSearch } = useSearchResultsPageContext();
  const navigateToSrp = useNavigateToSrp();

  return (
    <VendorSearchNavigationContext.Provider value={{ navigateToSrp: navigateToSrp(vendorSearch) }}>
      {children}
    </VendorSearchNavigationContext.Provider>
  );
};
